// EWM-866 — reconcile timing + selection helpers for AI Advisory streaming.
//
// Background: a corporate proxy (e.g. LBBW HASE/FONTUS) buffers the SSE response and severs
// the connection after ~5 minutes. The server keeps generating on an independent token (up to
// the streaming budget in SendMessageStreamCommandHandler, then via the reaper) and persists the assistant
// message, but the client only sees it by polling chat-history until it lands. These helpers
// are extracted as pure functions so the timing/selection logic can be unit-tested without
// standing up the React hook or fake fetch streams.

export interface ReconcileMessage {
  role: string;
  content: string;
  createdAt: string;
  messageId: string;
}

// Poll fast for the first phase so the common case (generation finishing shortly after the
// proxy drop) updates snappily, then ease off for the long reasoning tail to avoid hammering
// chat-history for the whole window.
export const RECONCILE_FAST_INTERVAL_MS = 4000;
export const RECONCILE_SLOW_INTERVAL_MS = 8000;
export const RECONCILE_FAST_PHASE_MS = 120_000; // 2 min at the fast interval

// Outlive the whole server-side rescue chain, not just the streaming budget: the SSE endpoint now
// hands a cut-off turn to the reaper (EWM-1089), which watches the stored response for up to 45
// minutes (AiAdvisoryReapGenerationTimeout). Giving up before that is what stranded users on a
// partial answer - the complete one landed later and nothing on screen went to fetch it. One
// minute of slack covers the final DB persist. Beyond this window the answer is still persisted;
// it just takes a reload to appear.
export const RECONCILE_MAX_DURATION_MS = 46 * 60 * 1000;

// How long to look for a persisted answer after a stream that carried NO content. A stream that
// delivered nothing is a transport or configuration problem (observed locally with a missing API
// key: the server closed the stream having "yielded no content"), so the fallback to the polling
// path should happen in seconds. The full window above is only right when real chunks arrived and
// the answer is genuinely on its way.
export const EMPTY_STREAM_RECONCILE_MS = 20_000;

// How long to wait for the SSE response to begin before giving up on streaming. A proxy that
// forbids SSE, or a server that sent headers and then died, both leave fetch() pending with no
// response at all - observed locally: the API sent 200, failed with "API key is not configured",
// and the client waited indefinitely with a blank bubble. From the client's side a hang is
// indistinguishable from a block, so both take the polling path after this deadline.
export const SSE_FIRST_RESPONSE_TIMEOUT_MS = 15_000;

/** Delay before the next poll, given how long reconciliation has been running. */
export function reconcilePollDelayMs(elapsedMs: number): number {
  return elapsedMs < RECONCILE_FAST_PHASE_MS
    ? RECONCILE_FAST_INTERVAL_MS
    : RECONCILE_SLOW_INTERVAL_MS;
}

/**
 * Did the server already accept this turn? The stream endpoint stores the user's message before
 * it starts generating, so finding that message means a generation is running (or has run) - and
 * a fallback must therefore WAIT for it rather than start a second one, which would give the user
 * two answers to one question.
 */
export function hasUserMessageWithContent<T extends ReconcileMessage>(
  messages: readonly T[],
  content: string,
): boolean {
  const needle = content.trim();
  return messages.some((m) => m.role.toLowerCase() === 'user' && m.content.trim() === needle);
}

/**
 * Pick the most recently created assistant message with content. A multi-turn conversation
 * keeps earlier assistant turns in history, so we must not reconcile against a stale one and
 * replace the live placeholder with old content. createdAt is ISO-8601 UTC, so a lexical
 * compare orders it correctly.
 */
export function pickLatestAssistantMessage<T extends ReconcileMessage>(
  messages: readonly T[],
): T | undefined {
  return messages
    .filter((m) => m.role.toLowerCase() === 'assistant' && Boolean(m.content))
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
}
